import { useRouter } from '@funtech-int/rival-react';
import React, { FC, PropsWithChildren, useEffect, useMemo } from 'react';

import { useSearchParams } from '@/shared/hooks/useSearchParams';
import { MovieModalContext } from '@/shared/constants';
import { useContentModal } from '@/shared/hooks/usePopups';
import { useWidget } from '@/shared/hooks/useWidget';
import { movieCardModalWidget } from '@/scenarios/movie';
import { QueryParameter } from '@/shared/constants/queryParams';

export const MovieModalProvider: FC<PropsWithChildren> = ({ children }) => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const contentModal = useContentModal();
    const MovieCardModalContent = useWidget(movieCardModalWidget);

    const controller = useMemo(() => {
        const updateSearchParams = () => {
            router.navigate({
                to: router.location,
                replace: true,
                meta: {
                    searchParams,
                },
            });
        };

        return {
            open(contentId: string) {
                searchParams.set(QueryParameter.REDIRECT_TO_FILM_PAGE, contentId);
                updateSearchParams();
            },
            close() {
                searchParams.delete(QueryParameter.REDIRECT_TO_FILM_PAGE);
                updateSearchParams();
            },
        };
    }, [searchParams, router]);

    const contentId = searchParams.get(QueryParameter.REDIRECT_TO_FILM_PAGE);
    const isContinueWatching = searchParams.get(QueryParameter.CONTINUE_WATCHING);
    const isPromo = searchParams.get(QueryParameter.PROMO);

    useEffect(() => {
        if (!contentId || isContinueWatching || isPromo) {
            return;
        }

        const id = contentModal.show({
            onClose: controller.close,
            content: <MovieCardModalContent contentId={contentId} />,
        });

        return () => {
            contentModal.hide(id);
        };
    }, [
        MovieCardModalContent,
        contentId,
        contentModal,
        controller.close,
        isContinueWatching,
        isPromo,
        router.location.id,
    ]);

    return <MovieModalContext.Provider value={controller}>{children}</MovieModalContext.Provider>;
};
